import type { ReactNode } from "react";
import HoraireDisplay from "./HoraireDisplay.tsx";

import { CadendarConsts, Days, dayToJour, HorairesDTO } from "cadendar-shared";
import HoraireLineEdit, { HoraireLineEditProps } from "./HoraireLineEdit";
import { DateTime } from "luxon";
import IconButton from "../../main/components/IconButton.tsx";
import ArrowLeft01Icon from "../../main/icons/arrow-left-01-stroke-rounded.tsx";
import ArrowRight01Icon from "../../main/icons/arrow-right-01-stroke-rounded.tsx";
import Button from "../../main/components/Button.tsx";

const { days } = CadendarConsts;

export interface HorairesPanelProps {
  horaire: HorairesDTO | undefined;
  displayedHoraireDate?: Date;
  onChoosePrevHoraire: () => void;
  onChooseNextHoraire: () => void;
  prevHoraireEnabled: boolean;
  nextHoraireEnabled: boolean;
  localHoraire: { [jour in Days]: HoraireLineEditProps["plages"] };
  setPrevEditWeek: () => void;
  setNextEditWeek: () => void;
  addPlage: (jour: Days) => void;
  removePlage: (jour: Days, position: number) => void;
  setPlages: (jour: Days, plages: { start: string; end: string }[]) => void;
  copyDayToNext?: (jour: Days) => void;
  isEditing: boolean;
  setIsEditing: () => void;
  saveNewHoraires: () => void;
  isSaving: boolean;
  onCancel: () => void;
  currentWeek: Date;
  validationMap: { [jour in Days]: { start: string[]; end: string[] }[] };
  content?: HorairesPanelContent;
  editDateControls?: ReactNode;
  existingFooter?: ReactNode;
  editingFooter?: ReactNode;
}

export interface HorairesPanelContent {
  currentTitle: string;
  currentDescription: string;
  planningTitle: string;
  planningDescription: string;
  draftDescription: string;
  createButtonTitle: string;
  validationErrorMessage: string;
  saveHintMessage: string;
  displayedDateLabel?: string;
  editDateLabel?: string;
}

const defaultContent: HorairesPanelContent = {
  currentTitle: "Horaires actuels",
  currentDescription:
    "Consultez l'organisation actuellement appliquée au cabinet.",
  planningTitle: "Planifier une nouvelle semaine",
  planningDescription:
    "Les nouveaux horaires prennent effet le lundi de la semaine choisie.",
  draftDescription:
    "Préparez ici les horaires qui s'appliqueront à une prochaine semaine.",
  createButtonTitle: "Créer de nouveaux horaires",
  validationErrorMessage:
    "Corrigez les horaires en erreur avant d'enregistrer.",
  saveHintMessage:
    "Les horaires seront appliqués à partir du lundi sélectionné.",
  displayedDateLabel: "Semaine du",
  editDateLabel: "Semaine du",
};

const HorairesPanel = (props: HorairesPanelProps) => {
  const content = props.content || defaultContent;
  const hasValidationErrors = Object.values(props.validationMap).some((errorPlages) =>
    errorPlages.some(
      (errorPlage) => errorPlage.start.length > 0 || errorPlage.end.length > 0
    )
  );

  const sectionClassName =
    "rounded-2xl border border-[var(--color-grey)] bg-white p-5 shadow-[0_14px_32px_rgba(0,0,0,0.06)]";
  const headerClassName =
    "mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between";
  const iconButtonDisabledClassName = "pointer-events-none opacity-40";

  return (
    <div className="flex flex-col gap-6 py-4 pb-8">
      <section className={sectionClassName}>
        <div className={headerClassName}>
          <div>
            <h2 className="m-0 text-xl font-semibold text-[var(--color-darker-blue)]">
              {content.currentTitle}
            </h2>
            <p className="mt-1 text-[var(--color-quasi-black)]">
              {content.currentDescription}
            </p>
          </div>
          {props.displayedHoraireDate ? (
            <div className="flex flex-wrap items-center gap-3">
              <IconButton
                onClick={
                  props.prevHoraireEnabled
                    ? props.onChoosePrevHoraire
                    : (event) => event.preventDefault()
                }
                className={
                  props.prevHoraireEnabled ? undefined : iconButtonDisabledClassName
                }
                aria-label="Horaires de la semaine précédente"
              >
                <ArrowLeft01Icon color="black" />
              </IconButton>
              <div className="min-h-[42px] rounded-full border border-[var(--color-grey)] bg-[var(--color-light-sky-blue)] px-4 py-2 text-[var(--color-darker-blue)]">
                {content.displayedDateLabel}
                <strong className="font-bold">
                  {" "}
                  {DateTime.fromJSDate(props.displayedHoraireDate).toLocaleString(
                    DateTime.DATE_MED
                  )}
                </strong>
              </div>
              <IconButton
                onClick={
                  props.nextHoraireEnabled
                    ? props.onChooseNextHoraire
                    : (event) => event.preventDefault()
                }
                className={
                  props.nextHoraireEnabled ? undefined : iconButtonDisabledClassName
                }
                aria-label="Horaires de la semaine suivante"
              >
                <ArrowRight01Icon color="black" />
              </IconButton>
            </div>
          ) : null}
        </div>

        {props.horaire ? <HoraireDisplay horaire={props.horaire} /> : null}
        {props.existingFooter ? (
          <div className="mt-4 border-t border-[var(--color-very-light-grey)] pt-4">
            {props.existingFooter}
          </div>
        ) : null}
      </section>

      <section className={sectionClassName}>
        <div className={headerClassName}>
          <div>
            <h2 className="m-0 text-xl font-semibold text-[var(--color-darker-blue)]">
              {content.planningTitle}
            </h2>
            <p className="mt-1 text-[var(--color-quasi-black)]">
              {content.planningDescription}
            </p>
          </div>
          {props.isEditing ? (
            <div className="flex flex-col items-start gap-3 md:items-end">
              <div className="flex flex-wrap items-center gap-3">
                <IconButton
                  onClick={props.setPrevEditWeek}
                  tooltip="Semaine précédente"
                  id="buttonPrevLundi"
                  aria-label="Semaine précédente"
                >
                  <ArrowLeft01Icon color="black" />
                </IconButton>
                <div className="min-h-[42px] rounded-full border border-[var(--color-grey)] bg-[var(--color-light-sky-blue)] px-4 py-2 text-[var(--color-darker-blue)]">
                  {content.editDateLabel}
                  <strong className="font-bold">
                    {" "}
                    {DateTime.fromJSDate(props.currentWeek).toLocaleString(
                      DateTime.DATE_MED
                    )}
                  </strong>
                </div>
                <IconButton
                  onClick={props.setNextEditWeek}
                  tooltip="Semaine suivante"
                  id="buttonNextLundi"
                  aria-label="Semaine suivante"
                >
                  <ArrowRight01Icon color="black" />
                </IconButton>
              </div>
              {props.editDateControls ? props.editDateControls : null}
            </div>
          ) : null}
        </div>

        {!props.isEditing ? (
          <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-[var(--color-dark-grey)] bg-[#fafafa] p-4 text-[var(--color-quasi-black)]">
            <p className="m-0">{content.draftDescription}</p>
            <Button
              onClick={() => props.setIsEditing()}
              id="nouveauxHorairesButton"
              title={content.createButtonTitle}
              style="main"
            />
          </div>
        ) : (
          <>
             <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 max-sm:grid-cols-1">
              {days.map((day, index) => (
                <div
                  className="rounded-2xl border border-[var(--color-grey)] bg-[#fbfcff] p-4"
                  key={day + "key"}
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="font-bold text-[var(--color-darker-blue)]">
                      {dayToJour(day)}
                    </div>
                    {props.copyDayToNext && index < days.length - 1 ? (
                      <Button
                        onClick={() => props.copyDayToNext!(day)}
                        title={`Recopier vers ${dayToJour(days[index + 1])}`}
                        style="secondary"
                        size="small"
                      />
                    ) : null}
                  </div>
                  <HoraireLineEdit
                    plages={props.localHoraire[day]}
                    addPlage={() => props.addPlage(day)}
                    removePlage={(position) => props.removePlage(day, position)}
                    onSetPlages={(plages) => props.setPlages(day, plages)}
                    validationMap={props.validationMap[day]}
                    day={day}
                  />
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-4 border-t border-[var(--color-very-light-grey)] pt-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-3 text-[var(--color-quasi-black)]">
                <div>
                  {hasValidationErrors
                    ? content.validationErrorMessage
                    : content.saveHintMessage}
                </div>
                {props.editingFooter ? props.editingFooter : null}
              </div>
              <div className="flex flex-wrap items-center gap-3 max-lg:justify-center">
                <Button
                  id="buttonSaveNewHoraires"
                  onClick={props.saveNewHoraires}
                  title={props.isSaving ? "Enregistrement..." : "Enregistrer"}
                  style="main"
                  disabled={hasValidationErrors || props.isSaving}
                />

                <Button
                  id="buttonCancelNewHoraires"
                  onClick={props.onCancel}
                  title="Annuler"
                  style="secondary"
                />
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default HorairesPanel;
